import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'

import { kAgentV2MatterApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ActivityFeedResponseSchema, type ActivityFeedResponse } from '@/matter/activity-feed/schemas'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/** A matter's most recent activity, newest first, capped by the endpoint. */
export async function getMatterActivity(matterId: string): Promise<ActivityFeedResponse> {
  const transactionId = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error("Could not retrieve user's authentication token.")

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kAgentV2MatterApiBasePath}${encodeURIComponent(matterId)}/activity`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Transaction-Id': transactionId,
    },
  })
  const { data, parseError } = await readResponseJson(response)

  if (!response.ok) {
    throw new ApiError(response.status, getErrorDetail(data) || `Failed to fetch matter activity: ${response.status}`)
  }

  if (parseError) {
    Sentry.captureException(new Error('Matter activity response JSON parse failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        matterId,
        parseError: formatUnknownError(parseError),
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid matter activity response JSON')
  }

  const parsed = ActivityFeedResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(new Error('Matter activity response schema validation failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        matterId,
        responseKeys: getTopLevelKeys(data),
        issues: parsed.error.issues,
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid matter activity response shape', parsed.error.issues)
  }

  return parsed.data
}

function getErrorDetail(data: unknown): string | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail
  if (detail === undefined || detail === null) return null
  return JSON.stringify(detail)
}
